import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
  Link,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { useBrandRatingQuery, useDeleteBrandRating } from "./hooks";

const renderTextWithLinks = (text) => {
  if (!text) {
    return null;
  }

  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </Link>
      );
    }
    return part;
  });
};

export default function BrandRatingDetail() {
  const { id: brandId, ratingId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useBrandRatingQuery(brandId, ratingId);
  const deleteBrandRating = useDeleteBrandRating(brandId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBrandRating.mutateAsync(ratingId);
      navigate(`/brands/${brandId}`);
    } catch (error) {
      console.error("Error deleting brand rating:", error);
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data?.rating) {
    return (
      <Box m={4}>
        <Typography variant="h4">Brand Rating not found</Typography>
      </Box>
    );
  }

  const { rating } = data;
  const answersMap = {};

  // Create a map of questionId to answer for easy lookup
  rating.answers?.forEach(answer => {
    answersMap[answer.brandRatingRubricQuestionId] = answer;
  });

  return (
    <Box m={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body1" color="text.secondary">
          <Link href={`/brands/${brandId}`} style={{ textDecoration: "underline", color: "#1976d2" }}>
            ← Back to Brand
          </Link>
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteClick}
          disabled={deleteBrandRating.isLoading}
        >
          Delete Rating
        </Button>
      </Box>

      <Box mb={4}>
        <Typography variant="h2" gutterBottom>
          Brand Rating Detail
        </Typography>

        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Scores:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {rating.rawScore && (
              <Chip
                label={`Raw Score: ${rating.rawScore}`}
                variant="outlined"
                size="medium"
                color="primary"
              />
            )}
            {rating.sectionScores && rating.sectionScores.map((sectionScore, index) => (
              <Chip
                key={index}
                label={`${sectionScore.section}: ${sectionScore.score}`}
                variant="outlined"
                size="medium"
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {rating.rubric?.category || "Unknown Category"}
        </Typography>
        {rating.rubric?.description && (
          <Typography variant="body1" color="text.secondary">
            {rating.rubric.description}
          </Typography>
        )}
      </Paper>

      {rating.rubric?.sections?.map((section) => (
        <Accordion key={section.id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">
              {section.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {section.sectionDescription && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {section.sectionDescription}
              </Typography>
            )}

            {section.subsections?.map((subsection) => (
              <Box key={subsection.id} mb={3}>
                <Typography variant="h6" gutterBottom>
                  {subsection.name}
                </Typography>
                {subsection.subsectionDescription && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {subsection.subsectionDescription}
                  </Typography>
                )}

                {subsection.questions?.map((question) => {
                  const answer = answersMap[question.id];

                  return (
                    <Card key={question.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {question.text}
                            </Typography>
                            {question.questionDetails && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {question.questionDetails}
                              </Typography>
                            )}
                          </Box>
                          {question.weight && (
                            <Chip
                              label={`Weight: ${question.weight}`}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 2 }}
                            />
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {answer
                          ? (
                            <Box>
                              <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <Typography variant="subtitle2">
                                Answer: <strong>{answer.answer}</strong>
                                </Typography>
                              </Box>

                              {answer.details && (
                                <Box mb={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                  Details:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {answer.details}
                                  </Typography>
                                </Box>
                              )}

                              {answer.citationQuotes && (
                                <Box mb={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                  Citation Quotes:
                                  </Typography>
                                  <Typography variant="subtitle2" color="text.secondary">
                                    {renderTextWithLinks(answer.citationQuotes)}
                                  </Typography>
                                </Box>
                              )}

                              {answer.citationUrls && (
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                  Sources:
                                  </Typography>
                                  <Typography variant="body2">
                                    <Link href={answer.citationUrls} target="_blank" rel="noopener noreferrer">
                                      {answer.citationUrls}
                                    </Link>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )
                          : (
                            <Typography variant="body2" color="text.secondary">
                              No answer recorded for this question
                            </Typography>
                          )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {(!rating.rubric?.sections || rating.rubric.sections.length === 0) && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No questions found for this rating
          </Typography>
        </Paper>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Brand Rating
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this brand rating? This action cannot be undone.
            All associated answers and data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteBrandRating.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteBrandRating.isLoading}
          >
            {deleteBrandRating.isLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
